import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { rawBoolStorage, syncJsonStorage } from '@/atoms/storage';
import type { HostedRowRef } from '@/data/hostedNoteAtoms';
import type { textAtomTypeId } from '@/enums';

export const activeBlockAtom = atom<{
    id: string | null;
    moveCaret?: 'start' | 'end';
}>({
    id: null,
    moveCaret: undefined,
});

export const paginatedFountainAtom = atomWithStorage<boolean>('editor_paginated_fountain', true, rawBoolStorage(true), {
    getOnInit: true,
});

// Browse-mode → character-chip-row handoff. `null` means browse mode owns the
// keyboard as usual; non-null means the scene-heading's character chip row
// (`EditorCharacters` for `sceneId`) currently owns Up/Down/Left/Right/Enter/Space.
// `blockId` lets the row re-select the exact heading block when exiting back to
// browse mode. `focus` picks which stop takes focus on entry: `first` (default
// — entered from browse or → from the time field) or `last` (the + add-combobox,
// when entered backward via ← / ↑ from the logline below). Set by `useBrowseMode`
// on ArrowRight over a single selected scene heading, by the time field's →, and
// by the logline's ← / ↑; cleared on exit (Arrow(Left|Up)-from-first / Esc).
export type ChipRowTarget = { sceneId: number; blockId: number; focus?: 'first' | 'last' };
export const browseChipRowAtom = atom<ChipRowTarget | null>(null);

// Browse-⌫ confirmation (#1840-04). True while «Удалить N блоков?» is up over
// the current browse selection; `useBrowseMode` raises and clears it, and the
// dialog it renders is the only thing that can delete.
//
// An atom rather than hook state because a second binding has to see it:
// ⌘D (`useDraftKeys`) listens on `document`, so it would otherwise still fire
// with the dialog open and change the very selection the dialog is asking
// about. What is open is enough — the blocks are read live off
// `state.browseSelection`, which the engine migrates across a background
// reparse where a frozen list of blockIds would go stale.
export const browseDeletePromptOpenAtom = atom(false);

// Set by `useSceneCreation` to ask the Fountain editor to move the caret into a
// just-created scene's heading once that scene's lines are rendered. Read and
// cleared by the scroll-to-scene effect in `useFountainEditorController`. Other paths
// that change `editor_scene` (table-view click, sidebar navigation) leave this
// null, so the caret stays where the user put it.
export const pendingSceneFocusIdAtom = atom<number | null>(null);

// sceneId whose scene-heading + logline focus group currently holds focus, or
// null. An empty logline collapses unless its scene's group is focused (the user
// is editing the heading or the logline itself). Set/cleared via the shared
// `useSceneFocusGroup` handlers on the heading wrapper and the logline wrapper.
export const focusedSceneHeadingAtom = atom<number | null>(null);

export const newBlockAtom = atom<{
    id: number | null;
    type: textAtomTypeId | null;
    text?: string;
} | null>({
    id: null,
    type: null,
});

// Caps prompt (spec §4.9, ADR 0009). Queued never-seen ALL-CAPS candidate
// names detected on an Enter commit — the engine reports the commit via the
// `lineCommitted` intent, `useCapsPrompt` scans it against the character
// pool and anchors the queue to the line whose submit produced it. Anchored
// by `lineId`, NOT `blockId`: blockId is regenerated on every scene reparse —
// including the reparse the firing commit itself triggers — so a blockId
// captured at commit time is already stale by the next render and may not
// match any current block, silently dropping the prompt. lineId survives
// that reparse. The prompt renders inside the anchor FountainBlock; Add/Skip
// pop the queue.
export const capsPromptAtom = atom<{
    sceneId: number;
    anchorLineId: number;
    queue: string[];
} | null>(null);

// Draft / note block counts, mirrored from the engine snapshot by
// `useDraftCountMirror` / `useNoteCountMirror` for UI outside the
// EditorEngineProvider. Nothing reads them at the moment: the «Скрыть
// черновики» / «Скрыть заметки» switches moved to `VisibilityPopover`, which
// doesn't gate on a zero count.
export const draftBlockCountAtom = atom(0);
export const noteBlockCountAtom = atom(0);

// «Заметки» whose inherited quote history is folded away (issue #1829-6). A
// reply's quote shows the WHOLE chain by default — every answer visible — and
// this is the set of EXCEPTIONS: uuids the reader has collapsed to just the
// fragment being answered.
//
// Storing the exceptions rather than the expanded ones is what keeps the set
// small and makes "expanded" the default for every note nobody has touched,
// including ones that arrive later from a teammate. (The pre-#1829 key
// `editor_expanded_note_quotes` stored the opposite polarity; it is left
// orphaned rather than migrated — the default flipping once per device is the
// intended outcome.)
//
// Deliberately a device-scoped VIEW preference and nothing else: it is not in
// the document, not in the `scene_note` atom, not in engine history, and
// changing it never dirties the save indicator — so folding a chain on one
// machine does not fold it on everyone else's. A uuid whose note has since
// been deleted is inert and costs nothing, so the set is never pruned.
export const collapsedNoteQuotesAtom = atomWithStorage<string[]>('editor_collapsed_note_quotes', [], syncJsonStorage<string[]>(), {
    getOnInit: true,
});

// The notes sidebar (glass panel over the editor). Session-only by decision:
// a translucent overlay auto-covering a third of the editor on every load
// would startle more than it helps, so the panel always starts closed — a
// plain atom, deliberately NOT atomWithStorage.
export const notesSidebarOpenAtom = atom(false);

// The sidebar gestures aimed at the document, in the order they were made,
// landed by `useNoteSidebarLanding` inside the paper. The panel renders OUTSIDE
// the EditorEngineProvider and its target may live in an episode the engine
// hasn't even loaded yet (the panel is project-wide, the editor per-episode),
// so every gesture goes through here: the panel switches `editor_episode_id`
// if needed and parks the gesture, and the landing effect fires as soon as the
// engine places the note — immediately when it's already loaded, after
// hydrate when it isn't. `reply` set means "splice a reply with this body";
// `editText` set means "commit this as the note's body"; `delete` set means
// "remove the note" (its replies stay — there is no cascade); none of them
// means "scroll to it, reveal if masked, flash".
//
// A QUEUE, not a slot: the panel stays usable while the paper is hidden
// («Show editor» off), and a second gesture must not overwrite a first that
// nobody has landed — its card already shows the edit as made. Park through
// `parkNoteSidebarGestureAtom`. The queue outlives the Editor screen (a plain
// atom in the app store), so gestures parked before leaving land the next time
// the paper mounts; closing the tab with any still queued asks first
// (`useParkedNoteGesturesUnloadGuard`). A gesture whose note is deleted
// underfoot is dropped by the panel, which sees the project's notes list.
export interface NoteSidebarGesture {
    noteId: string;
    reply?: string;
    editText?: string;
    delete?: true;
}
export const pendingNoteSidebarActionsAtom = atom<readonly NoteSidebarGesture[]>([]);

// Park one gesture at the back of the queue. A queued JUMP is dropped first:
// it is for finding, and only the latest gesture says where the reader is now.
export const parkNoteSidebarGestureAtom = atom(null, (get, set, gesture: NoteSidebarGesture) => {
    const queued = get(pendingNoteSidebarActionsAtom).filter(parked => !isNoteSidebarJump(parked));
    set(pendingNoteSidebarActionsAtom, [...queued, gesture]);
});

export const isNoteSidebarJump = (gesture: NoteSidebarGesture) =>
    gesture.reply === undefined && gesture.editText === undefined && !gesture.delete;

// The uuid of a «Заметка» on an act / episode header row that was just created
// and has not been rendered yet (#1840-18). Those notes are not engine facts —
// there is no marker to place, no focus claim, no pending-focus record — so the
// one thing a fresh one still needs, the caret in its empty body, is parked
// here: the gesture sets it, the card claims it on mount and clears it. An
// atom rather than component state because the doors (⌘⇧M, the context menu,
// the card's «Ответить») each hold their own hook instance and none of them
// renders the card.
export const pendingHostedNoteFocusAtom = atom<string | null>(null);

// A jump from the notes sidebar to a «Заметка» on an act / episode header row
// (#1840-18) — the hosted half of `pendingNoteSidebarActionsAtom`.
//
// Those notes are not engine facts, so there is no `noteBlockIdById` to wait on
// and no `revealNotes` command to run: the landing effect finds the scene whose
// heading carries the row (the sheet's own `getActEpisodeHeaderIndex`), scrolls
// to it and flashes the card, and the list the card is in peeks itself open if
// «Скрыть заметки» had collapsed it.
//
// Deliberately NOT cleared by the landing: two readers need it (the scroll and
// the reveal) and clearing would race them. `at` is what makes a repeat jump to
// the same note a new value, so the effect fires again.
export const hostedNoteJumpAtom = atom<{ noteId: string; parent: 'act' | 'fragment'; parentId: number; at: number } | null>(null);

// The ROWS of the current browse selection — the act / episode header rows it
// covers (#1841 items 13–14, [[0022-browse-stop-on-a-hosted-row]],
// [[0023-rows-ride-the-browse-selection]]).
//
// Browse selection is the engine's and it is a set of BLOCKS; these rows are
// deliberately not blocks, so the engine cannot hold them. The view holds its
// half here, beside the engine's own: the selection is the union, and a single
// selected row is simply a union whose block half is empty. The rows are few
// (a handful of groups on screen) and live only in browse mode.
//
// `beside` is what keeps the two halves one selection. It records the engine
// selection the rows were chosen WITH, as its lines' stable ids
// (`browseSelectionLines`): the moment the engine's half moves on without
// them — a paste landing framed, a delete re-seating the frame, a context-menu
// «Выделить все блоки», an undo — the safety effect in `useBrowseMode` sees
// the mismatch and lets the rows go. Line ids rather than block ids because a
// reparse re-mints every blockId, and a draft toggle over the very same
// selection is not the selection moving on. One helper writes the pair
// (`writeBrowseSelection`, beside the walk).
//
// A row is named by IDENTITY (`HostedRowRef`, compared by `noteRowKey`)
// rather than by its element: the plate has to survive the row re-rendering
// under it (a draft toggle rewrites the box).
export interface BrowseHostedRows {
    rows: readonly HostedRowRef[];
    beside: string;
}

export const NO_BROWSE_HOSTED_ROWS: BrowseHostedRows = { rows: [], beside: '' };

export const browseHostedRowsAtom = atom<BrowseHostedRows>(NO_BROWSE_HOSTED_ROWS);

// «Пропустить» on an underline: that one problem,
// in that one line, as its text — `issueSkipKey(lineId, code, matchedText)` —
// is left unpainted until the page reloads. Session memory, never stored: a
// skip is a "not now", unlike «Пропустить всё» (a pref).
export const skippedIssuesAtom = atom<ReadonlySet<string>>(new Set<string>());
